import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { validateEmail } from './utils/validation';
import RegisterTalentStep1 from './components/RegisterSteps/RegisterTalentStep1';
import RegisterTalentStep2MagicLink from './components/RegisterSteps/RegisterTalentStep2MagicLink';
import RegisterTalentStep3Info from './components/RegisterSteps/RegisterTalentStep3Info';
import Sidebar from './Sidebar';
import { Modal, Image, Button } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import icSuccess from './assets/img/ic-celebration-circle.svg';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';
import { trackEvent } from './services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from './services/analytics/analyticsEvents';
import { isProfileComplete, isRegistrationComplete } from './utils/userCompletion';

const Register = () => {
    const { user: authenticatedUser, setUser } = useContext(UserContext) || {};
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [step, setStep] = useState(() => authenticatedUser?._id ? 3 : 1);
    const [role, setRole] = useState('student');
    const [email, setEmail] = useState('');
    const [agree, setAgree] = useState(false);
    const [errors, setErrors] = useState({});
    const [detailsReady, setDetailsReady] = useState(false);
    const [detailsLoadError, setDetailsLoadError] = useState(false);
    const [detailsLoadAttempt, setDetailsLoadAttempt] = useState(0);
    const [missingFields, setMissingFields] = useState(null);
    const [resumeDetails, setResumeDetails] = useState(false);

    // Step 3 fields
    const [birthDate, setBirthDate] = useState('');
    const [citizenship1, setCitizenship1] = useState('');
    const [citizenship2, setCitizenship2] = useState('');
    const [country, setCountry] = useState('');
    const [city, setCity] = useState(''); 
    const [futureEducationalInstitution, setFutureEducationalInstitution] = useState('');
    const [plannedStudyDirection, setPlannedStudyDirection] = useState([]);
    const [plannedSpecialization, setPlannedSpecialization] = useState('');
    const [futureDegree, setFutureDegree] = useState('');
    const [plannedGraduationYear, setPlannedGraduationYear] = useState('');
    const [plannedGraduationQuarter, setPlannedGraduationQuarter] = useState('Q2');
    const [currentSemester, setCurrentSemester] = useState('');

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [mobileInvalidField, setMobileInvalidField] = useState(null);

    const makeLiveSetter = (fieldName, setterFn) => (val) => {
        setterFn(val);

        // remove field error as soon as user changes the field
        setFieldError(fieldName, null);

        // remove the general step3 message too (we’ll show it again on submit if still invalid)
        setFieldError('step3', null);

        // hide alert as soon as user starts fixing things
        setShowErrorMessage(false);
    };

    // live setters (clear errors + hide alert on change)
    const setBirthDateLive = makeLiveSetter('birthDate', setBirthDate);
    const setCitizenship1Live = makeLiveSetter('citizenship1', setCitizenship1);
    const setCitizenship2Live = makeLiveSetter('citizenship2', setCitizenship2);
    const setCountryLive = makeLiveSetter('country', setCountry);
    const setCityLive = makeLiveSetter('city', setCity);    
    const setFutureEducationalInstitutionLive = makeLiveSetter('futureEducationalInstitution', setFutureEducationalInstitution);
    const setPlannedStudyDirectionLive = makeLiveSetter('plannedStudyDirection', setPlannedStudyDirection);
    const setPlannedSpecializationLive = makeLiveSetter('plannedSpecialization', setPlannedSpecialization);
    const setFutureDegreeLive = makeLiveSetter('futureDegree', setFutureDegree);
    const setPlannedGraduationYearLive = makeLiveSetter('plannedGraduationYear', setPlannedGraduationYear);
    const setPlannedGraduationQuarterLive = makeLiveSetter('plannedGraduationQuarter', setPlannedGraduationQuarter);
    const setCurrentSemesterLive = makeLiveSetter('currentSemester', setCurrentSemester);
    

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const magic = params.get('magic');

        if (magic === '1') {
            setStep(3);

            // clean URL
            params.delete('magic');
            const qs = params.toString();
            const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    const navigate = useNavigate();

    useEffect(() => {
        if (step !== 3) return;
        let cancelled = false;
        setDetailsLoadError(false);
        api.get('/api/users/me').then(({ data }) => {
            if (cancelled) return;
            localStorage.setItem('user', JSON.stringify(data));
            if (typeof setUser === 'function') setUser(data);
            if (isRegistrationComplete(data)) {
                navigate('/discovery/companies', { replace: true });
                return;
            }
            setBirthDate(data.birthDate ? String(data.birthDate).slice(0, 10) : '');
            const citizenships = data.citizenships?.length ? data.citizenships : [data.nationality || ''];
            setCitizenship1(citizenships[0] || '');
            setCitizenship2(citizenships[1] || '');
            setCountry(data.country || '');
            setCity(data.city || '');
            setFutureEducationalInstitution(data.futureEducationalInstitution || '');
            setPlannedStudyDirection(data.plannedStudyDirection || []);
            setPlannedSpecialization(data.plannedSpecialization || '');
            setFutureDegree(data.futureDegree || '');
            setPlannedGraduationYear(data.plannedGraduationYear ?? '');
            setPlannedGraduationQuarter(data.plannedGraduationQuarter || 'Q2');
            setCurrentSemester(data.currentSemester ?? '');
            const resume = isProfileComplete(data) && Array.isArray(data.registrationMissingFields);
            setResumeDetails(resume);
            setMissingFields(resume ? data.registrationMissingFields : null);
            setDetailsReady(true);
        }).catch(() => {
            if (!cancelled) setDetailsLoadError(true);
        });
        return () => { cancelled = true; };
    }, [step, detailsLoadAttempt, navigate, setUser]);

    useEffect(() => {
        if (step === 3) {
            trackEvent(ANALYTICS_EVENTS.REGISTRATION_DETAILS_VIEWED, {
                role,
            });
        }
    }, [step, role]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const magic = params.get('magic');

        // don't track onboarding continuation as a fresh registration start
        if (magic === '1') return;

        trackEvent(ANALYTICS_EVENTS.REGISTRATION_STARTED, {
            role,
            initialStep: 1,
            initialStepName: 'email_capture',
            source: 'organic',
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setFieldError = (field, message) => {
        setErrors(prev => {
            const next = { ...prev };
            if (message) next[field] = message;
            else delete next[field];
            return next;
        });
    };

    useEffect(() => {
        if (!isMobile || !mobileInvalidField) return;

        const field = document.querySelector(`[data-register-field="${mobileInvalidField}"]`);
        if (!field) return;

        field.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setMobileInvalidField(null);
    }, [errors, isMobile, mobileInvalidField]);

    const checkEmailExists = async (emailToCheck) => {
        const res = await api.post('/api/auth/check-email', { email: emailToCheck });
        return !!res.data?.exists;
    };

    const handleEmailChange = (e) => {
        const val = e.target.value;
        setEmail(val);

        if (!val.trim()) {
            setFieldError('email', 'Email is required.');
            return;
        }

        if (!validateEmail(val)) {
            setFieldError('email', 'Please enter a valid email.');
            return;
        }

        setFieldError('email', null);
    };

    const handleEmailBlur = async () => {
        const val = email.trim();

        if (!val || !validateEmail(val)) return;

        try {
            const exists = await checkEmailExists(val);
            if (exists) setFieldError('email', 'This email is already registered.');
        } catch (err) {
            setFieldError('email', 'Could not verify email. Try again.');
        }
    };

    const normalizeText = (v) => String(v || '').trim();

    const validateStep3 = () => {
        const bd = normalizeText(birthDate);
        const fd = normalizeText(futureDegree);
        const c1 = normalizeText(citizenship1);
        const rc = normalizeText(country);
        const rct = normalizeText(city);
        const ei = normalizeText(futureEducationalInstitution);
        const gy = normalizeText(plannedGraduationYear);
        const cs = normalizeText(currentSemester);

        const sdArr = Array.isArray(plannedStudyDirection) ? plannedStudyDirection : [];
        const sdOk = sdArr.map(normalizeText).filter(Boolean);

        const currentYear = new Date().getFullYear();
        const yearNum = Number(gy);
        const semesterNum = Number(cs);

        const validationResults = [
            ['birthDate', !bd ? 'Birth date is required.' : null],
            ['citizenship1', !c1 ? 'Citizenship is required.' : null],
            ['country', !rc ? 'Country of residence is required.' : null],
            ['city', !rct ? 'City of residence is required.' : null],
            ['futureEducationalInstitution', !ei ? 'School / university is required.' : null],
            ['plannedStudyDirection', sdOk.length === 0 ? 'Planned study direction is required.' : null],
            ['futureDegree', !fd ? 'Future degree is required.' : null],
            [
                'plannedGraduationYear',
                !gy || !Number.isFinite(yearNum) || yearNum < currentYear || yearNum > 2200
                    ? `Graduation year must be ${currentYear} or later.`
                    : null,
            ],
            [
                'currentSemester',
                !cs || !Number.isInteger(semesterNum) || semesterNum < 1 || semesterNum > 12
                    ? 'Current semester is required.'
                    : null,
            ],
        ];

        const activeResults = validationResults.filter(([field]) =>
            !missingFields || missingFields.includes(field === 'citizenship1' ? 'citizenships' : field));
        activeResults.forEach(([field, message]) => setFieldError(field, message));
        const firstInvalidField = activeResults.find(([, message]) => Boolean(message))?.[0] || null;

        return { ok: !firstInvalidField, firstInvalidField };
    };

    const buildStep3Payload = () => {
        const c1 = normalizeText(citizenship1);
        const c2 = normalizeText(citizenship2);

        return {
            birthDate: normalizeText(birthDate),
            citizenships: [c1, c2].filter(Boolean).slice(0, 2),

            country: normalizeText(country),
            city: normalizeText(city),

            futureEducationalInstitution: normalizeText(futureEducationalInstitution),

            plannedStudyDirection: (Array.isArray(plannedStudyDirection) ? plannedStudyDirection : [])
                .map(normalizeText)
                .filter(Boolean),

            plannedSpecialization: normalizeText(plannedSpecialization),

            futureDegree: normalizeText(futureDegree),
            plannedGraduationYear: Number(normalizeText(plannedGraduationYear)),
            plannedGraduationQuarter: normalizeText(plannedGraduationQuarter),
            currentSemester: Number(normalizeText(currentSemester)),
        };
    };

    const handleNext = async (e) => {
        e?.preventDefault?.();

        if (step === 1) {
            const val = email.trim();

            if (!val) {
                setFieldError('email', 'Email is required.');
                return;
            }

            if (!validateEmail(val)) {
                setFieldError('email', 'Please enter a valid email.');
                return;
            }

            try {
                const exists = await checkEmailExists(val);
                if (exists) {
                    setFieldError('email', 'This email is already registered.');
                    return;
                }
                setFieldError('email', null);
            } catch (err) {
                setFieldError('email', 'Could not verify email. Try again.');
                return;
            }

            trackEvent(ANALYTICS_EVENTS.REGISTRATION_EMAIL_COMPLETED, {
                role,
                agreedToTerms: Boolean(agree),
            });

            setStep(2);
            return;
        }

        // Step 3 = save profile fields then go to donations
        if (step === 3) {
            if (!detailsReady) return;
            const { ok, firstInvalidField } = validateStep3();
            if (!ok) {
                setFieldError('step3', 'Please fix the highlighted fields.');
                setShowErrorMessage(true); // show alert
                if (isMobile) setMobileInvalidField(firstInvalidField);
                return;
            }

            setFieldError('step3', null);
            setShowErrorMessage(false); // hide alert

            try {
                const allValues = buildStep3Payload();
                const payload = missingFields
                    ? Object.fromEntries(Object.entries(allValues).filter(([field]) => missingFields.includes(field)))
                    : allValues;

                const res = await api.patch('/api/users/me', payload);

                localStorage.setItem('user', JSON.stringify(res.data.user));
                if (typeof setUser === 'function') setUser(res.data.user);

                if (resumeDetails) {
                    if (isRegistrationComplete(res.data.user)) {
                        navigate('/discovery/companies', { replace: true });
                    } else {
                        setFieldError('step3', 'Some registration details are still missing. Please reload and try again.');
                        setShowErrorMessage(true);
                    }
                    return;
                }

                trackEvent(ANALYTICS_EVENTS.REGISTRATION_DETAILS_COMPLETED, {
                    role,
                    futureDegree,
                    country,
                    city,
                    plannedStudyDirectionsCount: Array.isArray(plannedStudyDirection)
                        ? plannedStudyDirection.length
                        : 0,
                });

                setShowSuccessModal(true);
                return;
            } catch (err) {
                console.error('Step 3 save failed:', err);

                const status = err?.response?.status;
                const msg = err?.response?.data?.msg || err?.response?.data?.message;

                if (status === 401) {
                    setFieldError('step3', 'Session missing or expired. Please open the magic link again (from your email).');
                    setShowErrorMessage(true);
                    navigate('/', { replace: true }); // or '/register' if you prefer
                    return;
                }

                setFieldError('step3', msg || 'Could not save your details. Please try again.');
                setShowErrorMessage(true); // show alert on server error too
                return;
            }
        }

        setStep((prev) => prev + 1);
    };

    const handleCancel = () => {
        navigate('/');
    };

    return (
        <>
            {step === 1 && (
                <RegisterTalentStep1
                    email={email}
                    errors={errors}
                    onEmailChange={handleEmailChange}
                    onEmailBlur={handleEmailBlur}
                    agree={agree}
                    setAgree={setAgree}
                    onNext={handleNext}
                    onBack={handleCancel}
                />
            )}
            {step === 2 && (
                <RegisterTalentStep2MagicLink
                    email={email}
                    role={role}
                    agree={agree}
                    onBack={handleCancel}
                />
            )}
            {step === 3 && (
                <>
                    <Sidebar />
                    {!detailsReady ? (
                        <main className="main-content flex-grow-1 register-page">
                            <p>{detailsLoadError ? 'Could not load your saved details.' : 'Loading your saved details…'}</p>
                            {detailsLoadError && <Button onClick={() => setDetailsLoadAttempt(value => value + 1)}>Try again</Button>}
                        </main>
                    ) : <RegisterTalentStep3Info
                        missingFields={missingFields}
                        values={{
                            birthDate,
                            citizenship1,
                            citizenship2,
                            country,
                            city,
                            futureEducationalInstitution,
                            plannedStudyDirection,
                            plannedSpecialization,
                            futureDegree,
                            plannedGraduationYear,
                            plannedGraduationQuarter,
                            currentSemester,
                        }}
                        setters={{
                            setBirthDate: setBirthDateLive,
                            setCitizenship1: setCitizenship1Live,
                            setCitizenship2: setCitizenship2Live,
                            setCountry: setCountryLive,
                            setCity: setCityLive,
                            setFutureEducationalInstitution: setFutureEducationalInstitutionLive,
                            setPlannedStudyDirection: setPlannedStudyDirectionLive,
                            setPlannedSpecialization: setPlannedSpecializationLive,
                            setFutureDegree: setFutureDegreeLive,
                            setPlannedGraduationYear: setPlannedGraduationYearLive,
                            setPlannedGraduationQuarter: setPlannedGraduationQuarterLive,
                            setCurrentSemester: setCurrentSemesterLive,
                        }}
                        errors={errors}
                        onNext={handleNext}
                        onBack={() => {
                            trackEvent(ANALYTICS_EVENTS.REGISTRATION_MAYBE_LATER_CLICKED, {
                                destination: '/discovery/companies',
                            });

                            navigate('/discovery/companies', { replace: true });
                        }}
                        showErrorMessage={showErrorMessage}
                        setShowErrorMessage={setShowErrorMessage}
                        isMobile={isMobile}
                    />}
                </>
            )}

            {/* Success Modal */}
            {isMobile ? (
                <ProfileUpdateSuccessModal
                    show={showSuccessModal}
                    onHide={() => navigate('/discovery/companies', { replace: true })}
                    title="Welcome to aBROADIFY"
                    message="You're now on aBROADIFY. Please complete your profile to improve your matching."
                    icon={icSuccess}
                    actions={(
                        <>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    trackEvent(ANALYTICS_EVENTS.REGISTRATION_CONTINUE_TO_ONBOARDING_CLICKED, {
                                        destination: '/onboarding',
                                    });
                                    setShowSuccessModal(false);
                                    navigate('/onboarding', { replace: true });
                                }}
                            >
                                Complete Profile
                            </Button>

                            <Button
                                variant="outline-primary"
                                onClick={() => {
                                    trackEvent(ANALYTICS_EVENTS.REGISTRATION_EXPLORE_CLICKED, {
                                        destination: '/discovery/companies',
                                    });

                                    setShowSuccessModal(false);
                                    navigate('/discovery/companies', { replace: true });
                                }}
                            >
                                Explore aBROADIFY
                            </Button>
                        </>
                    )}
                />
            ) : (
                <Modal show={showSuccessModal} onHide={() => navigate('/discovery/companies', { replace: true })} centered>
                    <Modal.Header closeButton>
                        <Modal.Title></Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Image
                            src={icSuccess}
                            alt=""
                            className="d-block m-auto mb-4"
                        />
                        <h2 className="text-center mb-3">Welcome to aBROADIFY</h2>
                        <p className="text-center">
                            You're now on aBROADIFY. Please complete your profile to improve your matching.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-center">
                        <Button
                            variant="primary"
                            onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.REGISTRATION_CONTINUE_TO_ONBOARDING_CLICKED, {
                                    destination: '/onboarding',
                                });
                                setShowSuccessModal(false);
                                navigate('/onboarding', { replace: true });
                            }}
                            className="mb-3"
                        >
                            Complete Profile
                        </Button>

                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                trackEvent(ANALYTICS_EVENTS.REGISTRATION_EXPLORE_CLICKED, {
                                    destination: '/discovery/companies',
                                });

                                setShowSuccessModal(false);
                                navigate('/discovery/companies', { replace: true });
                            }}
                        >
                            Explore aBROADIFY
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default Register;
